import { z } from 'zod';

import authService from './auth/auth';
import { EnumJobType, EnumLevelLanguage } from './candidateProfile';
import { DjangoDataZodSchema } from './common';
import { apiRoutes } from './config';
import type { Industry } from './resume-checker';
import { industryConfigSchema, resumeAnalyzerSchema } from './resume-checker';

const resumeListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  updatedAt: z.coerce.date(),
  resumeScore: z.number(),
  generalStatus: z.string(),
  scoreStatus: z.string(),
});

const resumeDetailSchema = z.object({
  generalStatus: z.string(),
  id: z.number(),
  lastJobOfferId: z.number().nullable(),
  resumeName: z.string(),
  industryConfig: industryConfigSchema,
  name: z.string(),
  jobTitle: z.string(),
  phoneNumber: z.string(),
  email: z.string(),
  linkedinUrl: z.string(),
  address: z.string(),
  summary: z.string(),
  skills: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      skills: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          aiGenerated: z.boolean(),
        })
      ),
    })
  ),
  hobbies: z.array(z.string()),
  education: z.array(
    z.object({
      id: z.number().optional(),
      schoolName: z.string(),
      address: z.string(),
      degree: z.string(),
      major: z.string(),
      gpa: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      description: z.string(),
    })
  ),
  languages: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      level: z.nativeEnum(EnumLevelLanguage),
    })
  ),
  certificates: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      url: z.string(),
      date: z.string(),
      description: z.string(),
    })
  ),
  medicalLicenses: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      credentialType: z.enum([
        'LICENSE',
        'BOARD_CERTIFICATION',
        'PROFESSIONAL_CERTIFICATION',
        'TRAINING',
        '',
      ]),
      licenseNumber: z.string(),
      issuer: z.string(),
      state: z.string(),
      date: z.string(),
      expirationDate: z.string(),
    })
  ),
  workExperience: z.array(
    z.object({
      id: z.number().optional(),
      company: z.string(),
      jobTitle: z.string(),
      description: z.string(),
      address: z.string(),
      technologies: z.array(z.string()),
      bulletPoints: z.array(z.string()),
      industry: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      projects: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          description: z.string(),
          client: z.string(),
          url: z.string(),
          technologies: z.array(z.string()),
          industry: z.string(),
          startDate: z.string(),
          endDate: z.string(),
        })
      ),
      jobType: z.nativeEnum(EnumJobType),
    })
  ),

  extraActivities: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      description: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      oneDayEvent: z.boolean(),
    })
  ),
  links: z.array(
    z.object({
      id: z.number(),
      label: z.string(),
      url: z.string(),
    })
  ),
  projects: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      description: z.string(),
      url: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      technologies: z.array(z.string()),
    })
  ),
});

type ResumeDetailApi = z.infer<typeof resumeDetailSchema>;

export const transformBuilderApiData = ({
  name,
  phoneNumber,
  address,
  email,
  ...data
}: ResumeDetailApi) => {
  return {
    ...data,
    personalInformation: {
      name,
      phoneNumber,
      address,
      email,
    },
    links: {
      githubUrl: data.links.find(
        ({ label }) => label.toLowerCase() === 'github'
      )?.url,
      githubId: data.links.find(({ label }) => label.toLowerCase() === 'github')
        ?.id,
      linkedinUrl: data.links.find(
        ({ label }) => label.toLowerCase() === 'linkedin'
      )?.url,
      linkedinId: data.links.find(
        ({ label }) => label.toLowerCase() === 'linkedin'
      )?.id,
      links: data.links.filter(
        ({ label }) =>
          label.toLowerCase() !== 'github' && label.toLowerCase() !== 'linkedin'
      ),
    },
  };
};

export type ResumeDetail = ReturnType<typeof transformBuilderApiData>;

const trasnformBuilderData = ({
  personalInformation,
  ...data
}: Partial<ResumeDetail>) => {
  return {
    ...data,
    ...personalInformation,
    workExperience: data.workExperience?.map((workExperienceItem) => ({
      ...workExperienceItem,
      id:
        typeof workExperienceItem.id === 'number'
          ? workExperienceItem.id
          : undefined,
    })),
    languages: data.languages?.map(({ id, level, name }) => ({
      name: name || '',
      level: level || '',
      id,
    })),
    links: data.links && [
      { label: 'Github', url: data.links?.githubUrl, id: data.links.githubId },
      {
        label: 'LinkedIn',
        url: data.links?.linkedinUrl,
        id: data.links.linkedinId,
      },
      ...data.links.links.map(({ label, url, id }) => ({
        id,
        label: label ?? '',
        url: url ?? '',
      })),
    ],
  };
};

const transformSkills = (skills: { name: string; group: string }[]) => {
  const groupMap: Record<string, { name: string }[]> = {};

  for (const skill of skills) {
    if (!groupMap[skill.group]) {
      groupMap[skill.group] = [];
    }
    groupMap[skill.group].push({ name: skill.name });
  }

  return Object.entries(groupMap).map(([groupName, groupSkills]) => ({
    name: groupName,
    skills: groupSkills,
  }));
};

const getGenerateDescriptionSchema = z.object({
  description: z.string(),
});

const getGenerateSkillsSchema = z.array(
  z.object({
    name: z.string(),
    group: z.string(),
  })
);

const getGenerateSuggestionsSchema = z.object({
  courses: z.array(z.string()),
});

const getGenerateSummarySchema = z.object({
  summary: z.string(),
});

export const getResumeList = async () => {
  const { data } = await authService.get(apiRoutes.resumeBuilder._root);
  return DjangoDataZodSchema(resumeListItemSchema).parse(data);
};

export const getResumeDetail = async (id: number) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id)._root
  );
  const validatedData = resumeDetailSchema.parse(data);
  return transformBuilderApiData(validatedData);
};

export const postResume = ({
  cvFile,
  industry,
}: {
  cvFile: Blob | File;
  industry: Industry;
}) => {
  return authService.post(
    apiRoutes.resumeBuilder.fromFile,
    { cvFile, industry },
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

export const getResumeScore = async (id: number) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id).score
  );
  return resumeAnalyzerSchema.parse(data);
};

export const patchResume = async ({
  data,
  id,
}: {
  id: number;
  data: Partial<ResumeDetail>;
}) => {
  return authService.patch(
    apiRoutes.resumeBuilder.detail(id)._root,
    trasnformBuilderData(data)
  );
};

export const deleteResume = async (id: number) => {
  return authService.delete(apiRoutes.resumeBuilder.detail(id)._root);
};

export const getGenerateDescription = async ({
  id,
  entryId,
  entryType,
  generationType,
  text,
  jobTitle,
}: {
  id: number;
  text: string;
  generationType: 'generate' | 'finish' | 'rewrite';
  entryType: 'EXPERIENCE' | 'PERSONAL_PROJECT' | 'PROJECT_AT_WORK';
  entryId?: number;
  jobTitle: string;
}) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id).generateDescription,
    {
      params: {
        text,
        generation_type: generationType,
        entry_type: entryType,
        entry_id: entryId,
        job_title: jobTitle,
      },
    }
  );

  return getGenerateDescriptionSchema.parse(data);
};

export const getGenerateSkills = async ({
  id,
  jobTitle,
}: {
  id: number;
  jobTitle: string;
}) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id).generateSkills,
    { params: { job_title: jobTitle } }
  );
  return transformSkills(getGenerateSkillsSchema.parse(data));
};

export const getGenerateSuggestions = async ({
  entryId,
  entryType,
  id,
  jobTitle,
}: {
  entryType: 'EDUCATION_COURSES';
  entryId?: number;
  id: number;
  jobTitle: string;
}) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id).generateSuggestions,
    {
      params: {
        entry_id: entryId,
        entry_type: entryType,
        job_title: jobTitle,
      },
    }
  );
  return getGenerateSuggestionsSchema.parse(data);
};

export const getGenerateSummary = async ({
  generationType,
  id,
  text,
  jobTitle,
}: {
  id: number;
  generationType: 'generate' | 'finish' | 'rewrite';
  text: string;
  jobTitle: string;
}) => {
  const { data } = await authService.get(
    apiRoutes.resumeBuilder.detail(id).generateSummary,
    { params: { text, generation_type: generationType, job_title: jobTitle } }
  );
  return getGenerateSummarySchema.parse(data);
};

export const postAiIssueFix = async ({
  id,
  additionalInformation,
  sectionKey,
  fieldKey,
}: {
  id: number;
  sectionKey: string;
  fieldKey: string;
  additionalInformation: string;
}) => {
  const { data } = await authService.post(
    apiRoutes.resumeBuilder.detail(id).aiIssueFix,
    {
      sectionKey,
      fieldKey,
      additionalInformation,
    }
  );

  return data;
};
